/**
 * Onboarding Context
 *
 * Two distinct "done" states:
 *   hasDismissedOnboarding — user clicked "Skip Onboarding". Suppresses auto-show for the
 *                            session but the "Resume Onboarding" button stays visible so the
 *                            user can re-enter (important when they still have no companies).
 *                            React state only — resets on page refresh.
 *
 *   hasCompletedOnboarding — user clicked "Finish Setup". Hides both the auto-show and the
 *                            "Resume Onboarding" button. Persisted in sessionStorage.
 *                            Key: onboarding_completed_<userId>
 *
 *   onboarding_step_<userId> — last wizard step the user reached (sessionStorage).
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useUserContext } from '@/hooks/useUserContext';

interface OnboardingContextType {
  hasDismissedOnboarding: boolean;
  hasCompletedOnboarding: boolean;
  isWizardOpen: boolean;
  savedStep: number;
  openWizard: () => void;
  closeWizard: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void; // dev helper
  saveStep: (step: number) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const getCompletedKey = (userId: string) => `onboarding_completed_${userId}`;
const getStepKey      = (userId: string) => `onboarding_step_${userId}`;

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { data: userContext } = useUserContext();
  const userId = userContext?.userId ?? 'unknown';
  const completedKey = getCompletedKey(userId);
  const stepKey      = getStepKey(userId);

  const [hasDismissedOnboarding, setHasDismissedOnboarding] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(
    () => sessionStorage.getItem(completedKey) === 'true'
  );
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [savedStep, setSavedStep] = useState(
    () => parseInt(sessionStorage.getItem(stepKey) ?? '0', 10) || 0
  );

  const openWizard = useCallback(() => setIsWizardOpen(true), []);
  const closeWizard = useCallback(() => setIsWizardOpen(false), []);

  const saveStep = useCallback((step: number) => {
    sessionStorage.setItem(stepKey, String(step));
    setSavedStep(step);
  }, [stepKey]);

  // Skip Onboarding — suppresses auto-show this session, resume button stays visible.
  const dismissOnboarding = useCallback(() => {
    setHasDismissedOnboarding(true);
    setIsWizardOpen(false);
  }, []);

  // Finish Setup — permanently marks onboarding done for this session.
  const completeOnboarding = useCallback(() => {
    sessionStorage.setItem(completedKey, 'true');
    sessionStorage.removeItem(stepKey);
    setHasCompletedOnboarding(true);
    setHasDismissedOnboarding(false);
    setIsWizardOpen(false);
    setSavedStep(0);
  }, [completedKey, stepKey]);

  const resetOnboarding = useCallback(() => {
    sessionStorage.removeItem(completedKey);
    sessionStorage.removeItem(stepKey);
    setHasCompletedOnboarding(false);
    setHasDismissedOnboarding(false);
    setIsWizardOpen(true);
    setSavedStep(0);
  }, [completedKey, stepKey]);

  return (
    <OnboardingContext.Provider
      value={{
        hasDismissedOnboarding,
        hasCompletedOnboarding,
        isWizardOpen,
        savedStep,
        openWizard,
        closeWizard,
        dismissOnboarding,
        completeOnboarding,
        resetOnboarding,
        saveStep,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}
